import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Check } from "lucide-react";
import { RequiredMark } from "../required-mark";
import { cn } from "../utils";
export interface InputSelectionCardProps {
  /** Unique id */
  id?: string;
  /** Field name */
  name: string;
  /** Card title (primary label) */
  label: string;
  /** Supporting description inside the card. Accepts rich content (e.g. a link). */
  description?: React.ReactNode;
  /** Whether checked */
  checked?: boolean;
  /** Change handler — receives the new boolean value */
  onChange?: (checked: boolean) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Required */
  required?: boolean;
  /** Error text rendered below the card */
  error?: string;
  /** Helper text rendered below the card when there is no error */
  helperText?: string;
  /** Disabled */
  disabled?: boolean;
  /** Additional classes */
  className?: string;
}

/**
 * InputSelectionCard
 *
 * A single bordered card acting as a boolean form field. The whole card is the
 * click target; a hidden native checkbox keeps form submission and a11y intact.
 *
 * Stack multiple `InputSelectionCard` fields when the user can pick several
 * options — each card is its own boolean field, not a group.
 */
const InputSelectionCard = React.forwardRef<HTMLInputElement, InputSelectionCardProps>((t0, ref) => {
  const $ = _c(61);
  const {
    id,
    name,
    label,
    description,
    checked,
    onChange,
    onBlur,
    required,
    error,
    helperText,
    disabled,
    className
  } = t0;
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descriptionId = `${inputId}-description`;
  const cardDescriptionId = description ? `${inputId}-card-desc` : undefined;
  const hasError = Boolean(error);
  const hasDescription = Boolean(error || helperText);
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = React.useState(checked ?? false);
  const isChecked = isControlled ? checked : internalChecked;
  let t1;
  if ($[0] !== isControlled || $[1] !== onChange) {
    t1 = e => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e.target.checked);
    };
    $[0] = isControlled;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleChange = t1;
  let t2;
  if ($[3] !== className) {
    t2 = cn("flex w-full flex-col gap-2", className);
    $[3] = className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !isChecked && !hasError && "border-border-subtle bg-surface-base-primary";
  const t4 = isChecked && !hasError && "border-border-default bg-surface-base-interactive";
  const t5 = hasError && "border-input-border-error bg-surface-feedback-error";
  const t6 = disabled && "pointer-events-none opacity-50";
  let t7;
  if ($[5] !== t3 || $[6] !== t4 || $[7] !== t5 || $[8] !== t6) {
    t7 = cn("group/selection-card relative flex cursor-pointer items-center gap-4 rounded-lg border border-solid px-4 py-3 transition-colors", t3, t4, t5, t6, "focus-within:shadow-[0_0_0_2px_var(--color-border-focus)]");
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = hasError || undefined;
  const t9 = hasDescription ? descriptionId : undefined;
  let t10;
  if ($[10] !== disabled || $[11] !== handleChange || $[12] !== inputId || $[13] !== isChecked || $[14] !== name || $[15] !== onBlur || $[16] !== ref || $[17] !== required || $[18] !== t8 || $[19] !== t9) {
    t10 = <input ref={ref} type="checkbox" id={inputId} name={name} checked={isChecked} onChange={handleChange} onBlur={onBlur} disabled={disabled} required={required} aria-invalid={t8} aria-describedby={t9} className="peer sr-only" />;
    $[10] = disabled;
    $[11] = handleChange;
    $[12] = inputId;
    $[13] = isChecked;
    $[14] = name;
    $[15] = onBlur;
    $[16] = ref;
    $[17] = required;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = !isChecked && !hasError && "border-select-control-border bg-select-control-background-unchecked";
  const t12 = isChecked && !hasError && "border-select-control-background-checked bg-select-control-background-checked";
  const t13 = hasError && "border-input-border-error bg-select-control-background-unchecked";
  const t14 = isChecked && "[&>svg]:block";
  let t15;
  if ($[21] !== t11 || $[22] !== t12 || $[23] !== t13 || $[24] !== t14) {
    t15 = cn("flex size-5 shrink-0 items-center justify-center rounded-sm border-[1.5px] border-solid transition-colors", t11, t12, t13, "[&>svg]:hidden", t14);
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Check className="size-3.5 text-white" strokeWidth={3} />;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t15) {
    t17 = <span className={t15} aria-hidden="true">{t16}</span>;
    $[27] = t15;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = disabled && "text-input-text-disabled";
  let t19;
  if ($[29] !== t18) {
    t19 = cn("typography-component-sm-medium text-text-primary", t18);
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== required) {
    t20 = <RequiredMark required={required} />;
    $[31] = required;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== label || $[34] !== t19 || $[35] !== t20) {
    t21 = <span className={t19}>{label}{t20}</span>;
    $[33] = label;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== cardDescriptionId || $[38] !== description || $[39] !== disabled) {
    t22 = description && <span id={cardDescriptionId} className={cn("typography-body-sm-regular desktop:typography-body-md-regular text-text-secondary text-justify", disabled && "text-input-text-disabled")}>{description}</span>;
    $[37] = cardDescriptionId;
    $[38] = description;
    $[39] = disabled;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t21 || $[42] !== t22) {
    t23 = <span className="flex min-w-0 flex-1 flex-col gap-2">{t21}{t22}</span>;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== cardDescriptionId || $[45] !== inputId || $[46] !== t10 || $[47] !== t17 || $[48] !== t23 || $[49] !== t7) {
    t24 = <label htmlFor={inputId} className={t7} aria-describedby={cardDescriptionId}>{t10}{t17}{t23}</label>;
    $[44] = cardDescriptionId;
    $[45] = inputId;
    $[46] = t10;
    $[47] = t17;
    $[48] = t23;
    $[49] = t7;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== descriptionId || $[52] !== error || $[53] !== hasDescription || $[54] !== hasError || $[55] !== helperText) {
    t25 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{error || helperText}</p>;
    $[51] = descriptionId;
    $[52] = error;
    $[53] = hasDescription;
    $[54] = hasError;
    $[55] = helperText;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t2 || $[58] !== t24 || $[59] !== t25) {
    t26 = <div data-testid="selection-card" className={t2}>{t24}{t25}</div>;
    $[57] = t2;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
});
InputSelectionCard.displayName = "InputSelectionCard";
export { InputSelectionCard };